import { DndContext, DragOverlay } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Button,
  Div,
  ErrorToast,
  H3,
  Icon,
  Img,
  P,
  PlusIcon,
  SuccessToast,
  createBrandTemplateMenuCategory,
  deleteBrandTemplateMenuTypeItemById,
  noCategories,
  updateBrandTemplateMenuCategory,
  reportError,
} from '@vgrubs/components';
import { updateBrandTemplateMenuCategoryOrder } from 'components/src/api/brandTemplates.api';
import { ItemType } from 'components/src/types/menu.types';
import { Dispatch, SetStateAction, useState } from 'react';
import { useParams } from 'react-router';
import { AddCategoryModal } from './AddCategoryModal';
import { CategoryComponent } from './CategoryComponent';
import { DeleteCategoryModal } from './DeleteCategoryModal';
import {
  BTCategoryResponse,
  CreateBTMenuCategoryDto,
  UpdateBTMenuCategoryDto,
} from 'components/src/types/brand.types';
import { BTMenuResponse } from 'components/src/types/brand.types';

export const Categories = ({
  menu,
  setMenu,
  setLoading,
}: {
  menu: BTMenuResponse | null;
  setMenu: Dispatch<SetStateAction<BTMenuResponse | null>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
}) => {
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState<BTCategoryResponse | null>(null);
  const [deleteModal, setDeleteModal] = useState<number | null>(null);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [isSorting, setIsSorting] = useState<BTMenuResponse | null>(null);

  const { brandTemplateId } = useParams() as {
    brandTemplateId: string;
  };

  async function handleEditCategory(
    name: CreateBTMenuCategoryDto['name'],
    description: CreateBTMenuCategoryDto['description'],
    minItems: CreateBTMenuCategoryDto['minItems'],
    maxItems: CreateBTMenuCategoryDto['maxItems'],
    emptyForm: () => void
  ) {
    const body: UpdateBTMenuCategoryDto = {
      name,
      description,
      minItems,
      maxItems,
    };

    if (!name) return ErrorToast('Please enter category name');
    if (!editModal) return;
    if (minItems && minItems > editModal.items.length)
      return ErrorToast(
        'Minimum items cannot be greater than the number of items in the category'
      );
    if (minItems && maxItems && minItems > maxItems)
      return ErrorToast('Minimum items cannot be greater than maximum items');

    try {
      setLoading(true);

      const { data } = await updateBrandTemplateMenuCategory(
        brandTemplateId,
        editModal.id,
        body
      );
      emptyForm();
      SuccessToast(data.message);
      setMenu(data.data);
      setEditModal(null);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleAddCategory(
    name: CreateBTMenuCategoryDto['name'],
    description: CreateBTMenuCategoryDto['description'],

    emptyForm: () => void
  ) {
    const body: CreateBTMenuCategoryDto = {
      name,
      description,
    };

    if (!name) return ErrorToast('Please enter category name');

    try {
      setLoading(true);
      const { data } = await createBrandTemplateMenuCategory(
        brandTemplateId,
        body
      );
      emptyForm();
      setMenu(data.data);
      SuccessToast(data.message);
      setAddModal(false);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteCategory() {
    if (!deleteModal) return;
    try {
      setLoading(true);

      const { data } = await deleteBrandTemplateMenuTypeItemById(
        brandTemplateId,
        deleteModal,
        ItemType.Category
      );
      setMenu(data.data);
      SuccessToast(data.message);
      setLoading(false);
      setDeleteModal(null);
    } catch (error) {
      reportError(error);
    }
  }

  const AddCategoryButton = () => {
    return (
      <Button $selected onClick={() => setAddModal(true)}>
        Add Category
      </Button>
    );
  };

  function dragEndEvent(e: any) {
    const { over, active } = e;
    moveItem({ over, active });
  }

  const moveItem = ({
    over,
    active,
  }: {
    over: { id: number };
    active: { id: number };
  }) => {
    const items = menu?.categories;
    if (!items) return;

    const sortedCategories = arrayMove(
      items,
      items.findIndex((category) => category.id === active.id),
      items.findIndex((category) => category.id === over?.id)
    );

    setMenu((prev) => {
      if (!prev) return prev;
      return { ...prev, categories: sortedCategories };
    });
  };

  function handleDragStart(event: any) {
    setActiveId(event.active.id);
  }

  function handleDragEnd(event: any) {
    if (event.over) {
      setActiveId(null);
      dragEndEvent(event);
    }
  }

  async function handleSaveOrder() {
    try {
      setLoading(true);
      if (!menu?.categories) return;
      const list = menu.categories.map((category, index) => ({
        id: category.id,
        order: index + 1,
      }));
      const { data } = await updateBrandTemplateMenuCategoryOrder(
        brandTemplateId,
        list
      );
      setMenu(data.data);
      SuccessToast(data.message);
      setIsSorting(null);
      setLoading(false);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const SortingButtons = () => {
    return (
      <>
        {isSorting && (
          <Button
            $selected
            $small
            $gray
            $w="130px"
            $wSM="calc(50% - 8px)"
            onClick={() => {
              setMenu(isSorting);
              setIsSorting(null);
            }}
          >
            Cancel Sorting
          </Button>
        )}
        <Button
          $selected={!!isSorting}
          $small
          $w="130px"
          $wSM={!isSorting ? '100%' : 'calc(50% - 8px)'}
          onClick={() => {
            if (!!isSorting) {
              return handleSaveOrder();
            }
            setIsSorting(menu);
          }}
        >
          {isSorting ? 'Save Sorting' : 'Sort Categories'}
        </Button>
      </>
    );
  };

  return (
    <>
      <Div $flex $between $alignCenter $mb="20">
        <H3 $mb="0">Categories ({menu?.categories.length})</H3>
        <Div $flex $alignCenter $gap="12px">
          <Div $flex $gap="16px" $desktop>
            <SortingButtons />
          </Div>
          {menu?.categories && menu?.categories?.length > 0 && (
            <Icon $pointer onClick={() => setAddModal(true)}>
              <PlusIcon />
            </Icon>
          )}
        </Div>
      </Div>

      <Div $flex $gap="16px" $mobile $mb="20">
        <SortingButtons />
      </Div>

      <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
        <SortableContext
          items={menu?.categories?.map((task) => task.id) || []}
          strategy={verticalListSortingStrategy}
        >
          {menu?.categories?.map((item) => (
            <CategoryComponent
              key={item.id}
              item={item}
              onClick={(item) => setEditModal(item)}
              onClickDelete={(id) => setDeleteModal(id)}
              isSorting={!!isSorting}
            />
          ))}
        </SortableContext>
        <DragOverlay>
          {activeId ? (
            <CategoryComponent
              isSorting={!!isSorting}
              item={
                menu?.categories?.find(
                  (t) => activeId === t.id
                ) as BTCategoryResponse
              }
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {menu?.categories && menu?.categories?.length < 1 && (
        <Div $flex $column $alignCenter>
          <Img alt="" $width="180px" src={noCategories} $mb="24" />
          <H3 $mb="24" $center>
            No Categories
          </H3>
          <P $mb="32" $center>
            This provider currently don’t have <br /> menu categories.
          </P>
          <AddCategoryButton />
        </Div>
      )}

      <AddCategoryModal
        addModal={addModal}
        closeModal={() => {
          setAddModal(false);
        }}
        handleAddCategory={handleAddCategory}
      />

      <AddCategoryModal
        addModal={editModal}
        closeModal={() => {
          setEditModal(null);
        }}
        handleEditCategory={handleEditCategory}
        edit
      />

      <DeleteCategoryModal
        deleteModal={deleteModal}
        closeModal={() => {
          setDeleteModal(null);
        }}
        handleDeleteCategory={handleDeleteCategory}
      />
    </>
  );
};
